import React from "react";
import { useBluelagoon, Bluelagoon } from "@bluelagoon";
import Icon from "../components/Icon";
import Message from "../components/Message";
import Button from "../components/Button";
import { toast } from "sonner";
import { track } from "@crm/analytics";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Crown } from "lucide-react";
import OdooModulesPanel from "./OdooModulesPanel";

export function NewIntegrationModal({
    onClose,
    onSuccess,
    setBrowserIntegration,
    initialIntegrationType,
    INTEGRATION_TYPES,
    isEditing = false,
    integrationToEdit = null,
    isNameUniqueForType,
}) {
    const { isFeatureEnabled } = useAuth();
    const hasSqlAccess = isFeatureEnabled("sqlIntegration");
    const hasFacebookAdsAccess = isFeatureEnabled("facebookAds");

    const RESTRICTED_TYPES = {
        sql: { hasAccess: hasSqlAccess, label: "SQL Database" },
        facebook: { hasAccess: hasFacebookAdsAccess, label: "Facebook Ads" },
    };

    const [selectedType, setSelectedType] = React.useState(() => {
        if (integrationToEdit) {
            // If editing an existing integration, use its data

            // Ensure the editable name is present in the config for the form
            const existingConfig = { ...(integrationToEdit.config || {}) };
            if (
                existingConfig.name == null &&
                typeof integrationToEdit.name === "string" &&
                integrationToEdit.name.length > 0
            ) {
                existingConfig.name = integrationToEdit.name;
            }

            return {
                id: integrationToEdit.type,
                platform: integrationToEdit.platform,
                type: integrationToEdit.type,
                config: existingConfig,
                integrationId: integrationToEdit.id,
            };
        } else if (initialIntegrationType) {
            // Get the config fields from INTEGRATION_TYPES
            const typeConfig = INTEGRATION_TYPES.find(
                (t) => t.id === initialIntegrationType.id
            );
            const initialConfig = {};

            // Initialize config with default values from configFields
            if (typeConfig?.configFields) {
                Object.entries(typeConfig.configFields).forEach(
                    ([key, field]) => {
                        if (typeof field === "object") {
                            initialConfig[key] =
                                field.defaultValue || field.value || "";
                        } else {
                            initialConfig[key] = field;
                        }
                    }
                );
            }

            return {
                id: initialIntegrationType.id,
                platform: initialIntegrationType.id,
                type: initialIntegrationType.id,
                config: initialConfig,
                backendConfig: initialIntegrationType.backendConfig,
            };
        }

        // Default to browser with its config structure
        const browserType = INTEGRATION_TYPES.find((t) => t.id === "browser");
        const browserConfig = {};

        if (browserType?.configFields) {
            Object.entries(browserType.configFields).forEach(([key, field]) => {
                browserConfig[key] = field.defaultValue || field.value || "";
            });
        }

        return {
            id: "browser",
            platform: "browser",
            type: "browser",
            config: browserConfig,
        };
    });
    const [searchTerm, setSearchTerm] = React.useState("");

    // Scope group selection for integrations with scopeGroups (e.g. microsoft-graph)
    const [selectedScopeGroups, setSelectedScopeGroups] = React.useState(() => {
        const type = selectedType
            ? INTEGRATION_TYPES.find((t) => t.id === selectedType.id)
            : null;
        if (!type?.backendConfig?.scopeGroups) return {};
        const defaults = {};
        type.backendConfig.scopeGroups.forEach((g) => {
            defaults[g.id] = !!g.default;
        });
        return defaults;
    });

    // Add test connection states
    const [testConnectionLoading, setTestConnectionLoading] =
        React.useState(false);
    const [testConnectionResult, setTestConnectionResult] =
        React.useState(null);

    const { call, isLoading } = useBluelagoon({
        method: isEditing ? "updateIntegration" : "createIntegration",
    });

    // Edit-flow: fetch the full integration row by id and merge its
    // (redacted) config into selectedType. The /integrations LIST
    // endpoint deliberately drops `config` so credentials never leak
    // through the row-list response — the parent's `integrationToEdit`
    // therefore arrives without `config` populated, and the form would
    // open with empty fields. Calling the dedicated single-row
    // endpoint here gives us the configuration shape with credentials
    // (password, apiKey, OAuth/refresh tokens) replaced by `null`
    // sentinels — non-secret metadata (host, port, database, region,
    // bucket, etc.) round-trips unchanged so the form can pre-populate
    // it. The user re-enters credentials only if they want to change
    // them; on update, empty/null credential fields signal "preserve
    // existing".
    const { call: fetchIntegrationDetails } = useBluelagoon({
        method: "getIntegration",
    });
    const [isLoadingExisting, setIsLoadingExisting] = React.useState(
        !!integrationToEdit
    );

    React.useEffect(() => {
        if (!integrationToEdit?.id) {
            setIsLoadingExisting(false);
            return;
        }
        let cancelled = false;
        setIsLoadingExisting(true);
        fetchIntegrationDetails({ integrationId: integrationToEdit.id })
            .then((res) => {
                if (cancelled) return;
                const full = res?.data ?? res;
                const fullConfig = full?.config;
                if (fullConfig && typeof fullConfig === "object") {
                    setSelectedType((prev) => {
                        // Preserve any user-typed `name` already in the
                        // form (the initializer above seeds it from
                        // integrationToEdit.name when missing).
                        const preservedName = prev?.config?.name;
                        const merged = { ...prev.config, ...fullConfig };
                        if (preservedName != null) merged.name = preservedName;
                        return { ...prev, config: merged };
                    });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                // Surface the failure rather than silently rendering
                // blank fields. The rejection from useBluelagoon is
                // `error.response.data` (the server body for 4xx/5xx),
                // shape `{ status, error }` — pull the human-readable
                // message and show it. Then close the modal so the
                // user doesn't accidentally submit an empty form that
                // would overwrite the existing config with blanks.
                const message =
                    err?.error ||
                    err?.message ||
                    "Could not load this integration. It may have been deleted, archived, or you may have lost access. Refresh the list and try again.";
                toast.error(message);
                if (typeof onClose === "function") onClose();
            })
            .finally(() => {
                if (!cancelled) setIsLoadingExisting(false);
            });
        return () => {
            cancelled = true;
        };
        // Refetch only when the underlying integration id changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integrationToEdit?.id]);

    // Add test connection handler
    const handleTestConnection = async () => {
        if (selectedType.id !== "sql" && selectedType.id !== "odoo") return;

        setTestConnectionLoading(true);
        setTestConnectionResult(null);

        try {
            let testParams;
            let response;

            if (selectedType.id === "sql") {
                testParams = {
                    integrationId: integrationToEdit?.id ?? "test",
                    config: {
                        tokensEncrypted:
                            integrationToEdit?.config?.tokensEncrypted ?? false,
                        databaseType: selectedType.config.databaseType,
                        host: selectedType.config.host,
                        port: selectedType.config.port
                            ? parseInt(selectedType.config.port)
                            : undefined,
                        username: selectedType.config.username,
                        password: selectedType.config.password,
                        database: selectedType.config.database,
                        schema: selectedType.config.schema,
                        filename: selectedType.config.filename,
                        ssl: selectedType.config.ssl,
                        connectTimeout: selectedType.config.connectTimeout
                            ? parseInt(selectedType.config.connectTimeout)
                            : 10000,
                        // BigQuery-specific fields
                        ...(selectedType.config.databaseType === "bigquery" && {
                            projectId: selectedType.config.projectId,
                            credentials: selectedType.config.credentials,
                            dataset: selectedType.config.dataset,
                            location: selectedType.config.location || "US",
                        }),
                    },
                };

                // Remove undefined values
                Object.keys(testParams).forEach((key) => {
                    if (
                        testParams[key] === undefined ||
                        testParams[key] === ""
                    ) {
                        delete testParams[key];
                    }
                });

                response = await Bluelagoon.testSQLConnection(testParams);
            } else if (selectedType.id === "odoo") {
                testParams = {
                    integrationId: integrationToEdit?.id ?? "test",
                    config: {
                        tokensEncrypted:
                            integrationToEdit?.config?.tokensEncrypted ?? false,
                        url: selectedType.config.url,
                        database: selectedType.config.database,
                        username: selectedType.config.username,
                        password: selectedType.config.password,
                    },
                };

                response = await Bluelagoon.testOdooConnection(testParams);
            }

            if (response.data.status === 200) {
                setTestConnectionResult({
                    success: true,
                    message: response.data.message || "Connection successful!",
                    connectionInfo: response.data.data?.connectionInfo,
                });
                toast.success(
                    selectedType.id === "odoo"
                        ? "Odoo connection successful!"
                        : "Database connection successful!"
                );
            } else {
                setTestConnectionResult({
                    success: false,
                    message: response.data.message || "Connection failed",
                });
                toast.error(
                    response.data.message ||
                        (selectedType.id === "odoo"
                            ? "Odoo connection failed"
                            : "Database connection failed")
                );
            }
        } catch (error) {
            console.error("Test connection error:", error);
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Connection test failed";
            setTestConnectionResult({ success: false, message: errorMessage });
            toast.error(errorMessage);
        } finally {
            setTestConnectionLoading(false);
        }
    };

    // Helper function to check if required SQL fields are filled
    const areRequiredSQLFieldsFilled = () => {
        if (selectedType.id !== "sql") return false;

        const config = selectedType.config || {};
        const databaseType = config.databaseType;

        if (!databaseType) return false;

        // Check required fields based on database type
        if (databaseType === "sqlite") {
            return config.filename;
        } else if (databaseType === "bigquery") {
            return config.projectId && config.credentials;
        } else {
            return (
                config.host &&
                config.username &&
                config.password &&
                config.database
            );
        }
    };

    // Helper function to check if required Odoo fields are filled
    const areRequiredOdooFieldsFilled = () => {
        if (selectedType.id !== "odoo") return false;

        const config = selectedType.config || {};
        return (
            config.url && config.database && config.username && config.password
        );
    };

    const handleLoginWithOAuth = async (type, integrationId) => {
        try {
            // For Shopify, validate shop name first
            if (type.id === "shopify") {
                const shopName = selectedType.config.shop;
                if (!shopName) {
                    toast.error("Please enter your Shopify shop name first");
                    return;
                }
            }

            // Build scopes: use scopeGroups if available, otherwise flat scopes
            let scopes = type.backendConfig.scopes;
            if (type.backendConfig.scopeGroups) {
                const base = type.backendConfig.baseScopes || [];
                const groupScopes = type.backendConfig.scopeGroups
                    .filter((g) => selectedScopeGroups[g.id])
                    .flatMap((g) => g.scopes);
                scopes = [...new Set([...base, ...groupScopes])];
            }

            const response = await Bluelagoon[type.backendConfig.method]({
                integrationId,
                scopes,
                // Add shop name for Shopify
                ...(type.id === "shopify" && {
                    shop: selectedType.config.shop,
                }),
            });

            // open a new tab with the url
            window.open(
                response.data.data.url,
                "_blank",
                "noopener,noreferrer"
            );
        } catch (error) {
            toast.error(
                "Sorry there was an error, please try again or contact support"
            );
        }
    };

    const filteredTypes = INTEGRATION_TYPES.filter((type) =>
        type.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Clear test connection result and reset scope groups when selected type changes
    React.useEffect(() => {
        setTestConnectionResult(null);
        const type = INTEGRATION_TYPES.find((t) => t.id === selectedType?.id);
        if (type?.backendConfig?.scopeGroups) {
            const defaults = {};
            type.backendConfig.scopeGroups.forEach((g) => {
                defaults[g.id] = !!g.default;
            });
            setSelectedScopeGroups(defaults);
        }
    }, [selectedType?.id]);

    const setValueInConfig = (key, value) => {
        setSelectedType({
            ...selectedType,
            config: { ...selectedType.config, [key]: value },
        });

        // Clear test connection result when configuration changes
        if (testConnectionResult) {
            setTestConnectionResult(null);
        }
    };

    const handleCreate = async (type) => {
        const restriction = RESTRICTED_TYPES[type.id];
        if (restriction && !restriction.hasAccess) {
            toast.error(
                `${restriction.label} integration is available on Innovator plan ($100/month) and above. Upgrade your plan to use this integration.`
            );
            return;
        }

        if (type.id === "browser" && !selectedType) {
            // Initialize config from INTEGRATION_TYPES when selecting new type
            const typeConfig = INTEGRATION_TYPES.find((t) => t.id === type.id);
            const initialConfig = {};

            if (typeConfig?.configFields) {
                Object.entries(typeConfig.configFields).forEach(
                    ([key, field]) => {
                        initialConfig[key] =
                            field.defaultValue || field.value || "";
                    }
                );
            }

            setSelectedType({
                ...type,
                config: initialConfig,
            });
            return;
        }

        try {
            const integrationName =
                selectedType.config.name || type.name + " Integration";

            // Validate name uniqueness for the integration type
            if (
                isNameUniqueForType &&
                !isNameUniqueForType(
                    integrationName,
                    type.id,
                    isEditing ? selectedType.integrationId : null
                )
            ) {
                toast.error(
                    `An integration with the name "${integrationName}" already exists for ${type.name}. Please choose a different name.`
                );
                return;
            }

            const requestData = {
                name: integrationName,
                platform: type.id,
                type: type.type || type.id,
                config: selectedType.config,
            };

            // Add integrationId if editing
            if (isEditing && selectedType.integrationId) {
                requestData.integrationId = selectedType.integrationId;
            }

            const response = await call(requestData);

            track("New Integration", {
                name: type.name,
                type: type.id,
                platform: selectedType.platform,
            });

            if (type.backendConfig?.useOAuth && !isEditing) {
                handleLoginWithOAuth(type, response.data?.integration?.id);

                return;
            }

            if (response?.data?.integration) {
                // if (type.id === "browser") {
                //     setBrowserIntegration(response.data.browserSession);
                // }
                onSuccess?.(response.data.integration);
                onClose();
            }
        } catch (error) {
            console.error(
                `Failed to ${isEditing ? "update" : "create"} integration:`,
                error
            );

            // Show user-friendly error message
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                `Failed to ${
                    isEditing ? "update" : "create"
                } integration. Please try again.`;
            toast.error(errorMessage);
        }
    };

    // Helper function to check if a field should be shown based on dependsOn
    const shouldShowField = (field, key) => {
        if (!field.dependsOn) return true;

        const dependencyValue = selectedType.config[field.dependsOn.field];
        return field.dependsOn.values.includes(dependencyValue);
    };

    // Helper function to render different input types
    const renderInputField = (key, field) => {
        if (field.type === "select") {
            return (
                <select
                    className="w-full p-2 border rounded-md"
                    value={selectedType.config[key] || field.defaultValue || ""}
                    onChange={(e) => setValueInConfig(key, e.target.value)}
                >
                    {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            );
        }

        if (field.type === "checkbox") {
            return (
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        className="mr-2"
                        checked={
                            selectedType.config[key] ||
                            field.defaultValue ||
                            false
                        }
                        onChange={(e) =>
                            setValueInConfig(key, e.target.checked)
                        }
                    />
                    <span className="text-sm text-gray-600">
                        {field.helpText}
                    </span>
                </div>
            );
        }

        // Special handling for Shopify shop field
        if (key === "shop" && selectedType.id === "shopify") {
            return (
                <div className="relative">
                    <input
                        type={field.type}
                        placeholder={field.placeholder}
                        className="w-full p-2 border rounded-md pr-32"
                        defaultValue={field.defaultValue || ""}
                        value={selectedType.config[key] || field.value || ""}
                        onChange={(e) => {
                            // Remove .myshopify.com if user tries to paste it
                            const value = e.target.value.replace(
                                ".shopify.com",
                                ""
                            );
                            setValueInConfig(key, value);
                        }}
                        readOnly={field.readOnly}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                        .shopify.com
                    </div>
                </div>
            );
        }

        // Default input field
        const inputValue = selectedType.config[key] || field.value || "";

        // Check if this is a name field and validate uniqueness
        const isNameField = key === "name";
        const isNameTaken =
            isNameField &&
            inputValue &&
            isNameUniqueForType &&
            !isNameUniqueForType(
                inputValue,
                selectedType.id,
                isEditing ? selectedType.integrationId : null
            );

        return (
            <div>
                <input
                    type={field.type}
                    placeholder={field.placeholder}
                    className={`w-full p-2 border rounded-md ${
                        field.readOnly ? "bg-gray-50" : ""
                    } ${
                        isNameTaken ? "border-red-300 focus:border-red-500" : ""
                    }`}
                    defaultValue={field.defaultValue || ""}
                    value={inputValue}
                    onChange={(e) => setValueInConfig(key, e.target.value)}
                    readOnly={field.readOnly}
                />
                {isNameField && inputValue && isNameUniqueForType && (
                    <div className="mt-1 text-sm">
                        {isNameTaken ? (
                            <span className="text-red-600 flex items-center">
                                <Icon name="X" className="w-4 h-4 mr-1" />
                                This name is already taken
                            </span>
                        ) : (
                            <span className="text-green-600 flex items-center">
                                <Icon name="Check" className="w-4 h-4 mr-1" />
                                Name is available
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderConfigurationForm = (type) => {
        const integrationConfig = INTEGRATION_TYPES.find(
            (t) => t.id === type.id
        );
        if (!integrationConfig) return null;

        // Special handling for Shopify to show shop name input before OAuth
        const isShopify = type.id === "shopify";
        const showOAuthButton =
            type.backendConfig?.useOAuth && !isShopify && !isEditing;

        return (
            <Dialog open={true} onOpenChange={onClose}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>
                            {isEditing ? "Edit" : "Configure"}{" "}
                            {integrationConfig.name} Integration
                        </DialogTitle>
                        <DialogDescription>
                            {integrationConfig.helpText}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto space-y-4 px-1">
                        {/* Show capabilities if available */}
                        {integrationConfig.capabilities && (
                            <div className="mb-6">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                                        <Icon
                                            name="Info"
                                            className="w-4 h-4 mr-2"
                                        />
                                        What you can do with this integration:
                                    </h4>
                                    <ul className="space-y-2">
                                        {integrationConfig.capabilities.map(
                                            (capability, index) => (
                                                <li
                                                    key={index}
                                                    className="text-sm text-blue-800 flex items-start"
                                                >
                                                    <Icon
                                                        name="Check"
                                                        className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0"
                                                    />
                                                    {capability}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            </div>
                        )}
                        {integrationConfig.alert && (
                            <div className="mb-6">
                                <Message warning>
                                    <ul className="list-disc list-inside space-y-1">
                                        {integrationConfig.alert.map(
                                            (alert, index) => (
                                                <li key={index}>{alert}</li>
                                            )
                                        )}
                                    </ul>
                                </Message>
                            </div>
                        )}

                        {/* Show installed Odoo modules when editing */}
                        {isEditing && type.id === "odoo" && integrationToEdit?.id && (
                            <div className="mb-6">
                                <OdooModulesPanel integrationId={integrationToEdit.id} />
                            </div>
                        )}

                        {/* Show config fields for Shopify or non-OAuth integrations */}
                        {(!type.backendConfig?.useOAuth || isShopify) &&
                            Object.entries(integrationConfig.configFields)
                                .filter(
                                    ([key, field]) =>
                                        field.type !== "hidden" &&
                                        field.type !== "array" &&
                                        !field.isHidden &&
                                        shouldShowField(field, key)
                                )
                                .map(([key, field]) => (
                                    <div className="space-y-2" key={key}>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {key.charAt(0).toUpperCase() +
                                                key.slice(1)}
                                            {field.required && (
                                                <span className="text-red-500 ml-1">
                                                    *
                                                </span>
                                            )}
                                            {field.optional && (
                                                <span className="text-gray-500 ml-1">
                                                    (optional)
                                                </span>
                                            )}
                                            {field.helpText && (
                                                <span className="ml-2 text-xs text-gray-500">
                                                    ({field.helpText})
                                                </span>
                                            )}
                                        </label>
                                        {renderInputField(key, field)}
                                    </div>
                                ))}

                        {/* For OAuth integrations (except Shopify), still allow editing the Name field */}
                        {type.backendConfig?.useOAuth &&
                            !isShopify &&
                            integrationConfig.configFields?.name && (
                                <div className="space-y-2" key="name">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Name
                                        {integrationConfig.configFields.name
                                            .required && (
                                            <span className="text-red-500 ml-1">
                                                *
                                            </span>
                                        )}
                                        {integrationConfig.configFields.name
                                            .optional && (
                                            <span className="text-gray-500 ml-1">
                                                (optional)
                                            </span>
                                        )}
                                        {integrationConfig.configFields.name
                                            .helpText && (
                                            <span className="ml-2 text-xs text-gray-500">
                                                (
                                                {
                                                    integrationConfig
                                                        .configFields.name
                                                        .helpText
                                                }
                                                )
                                            </span>
                                        )}
                                    </label>
                                    {renderInputField(
                                        "name",
                                        integrationConfig.configFields.name
                                    )}
                                </div>
                            )}

                        {/* Scope group selection for integrations with scopeGroups */}
                        {type.backendConfig?.scopeGroups && !isEditing && (
                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Select permissions
                                </label>
                                <p className="text-xs text-gray-500">
                                    Choose which Microsoft 365 services to enable. You can reconnect later to change permissions.
                                </p>
                                <div className="space-y-2">
                                    {type.backendConfig.scopeGroups.map((group) => {
                                        const isSelected = !!selectedScopeGroups[group.id];
                                        const hasRequires = group.requires?.length > 0;
                                        const requiresMet = !hasRequires || group.requires.every((r) => selectedScopeGroups[r]);
                                        const isDisabled = hasRequires && !requiresMet;

                                        return (
                                            <button
                                                key={group.id}
                                                type="button"
                                                onClick={() => {
                                                    if (isDisabled) return;
                                                    setSelectedScopeGroups((prev) => {
                                                        const next = { ...prev, [group.id]: !prev[group.id] };
                                                        // If unchecking, also uncheck groups that require this one
                                                        if (prev[group.id]) {
                                                            type.backendConfig.scopeGroups.forEach((g) => {
                                                                if (g.requires?.includes(group.id)) {
                                                                    next[g.id] = false;
                                                                }
                                                            });
                                                        }
                                                        return next;
                                                    });
                                                }}
                                                disabled={isDisabled}
                                                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                                                    isDisabled
                                                        ? "opacity-40 cursor-not-allowed border-gray-200 bg-gray-50"
                                                        : isSelected
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-gray-200 bg-white hover:border-gray-300"
                                                }`}
                                            >
                                                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                                                    isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"
                                                }`}>
                                                    {isSelected && (
                                                        <Icon name="Check" className="w-3 h-3 text-white" />
                                                    )}
                                                </div>
                                                {group.icon && (
                                                    <img src={group.icon} alt="" className="w-5 h-5 flex-shrink-0" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {group.label}
                                                        </span>
                                                        {group.adminConsent && (
                                                            <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                                                                Admin consent
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {group.description}
                                                    </p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Test Connection for SQL integrations */}
                        {type.id === "sql" &&
                            (!type.backendConfig?.useOAuth || isShopify) && (
                                <div className="space-y-3">
                                    <Button
                                        variant="outline"
                                        onClick={handleTestConnection}
                                        disabled={
                                            testConnectionLoading ||
                                            !areRequiredSQLFieldsFilled()
                                        }
                                        isLoading={testConnectionLoading}
                                        className="w-full"
                                    >
                                        <Icon
                                            name="Database"
                                            className="w-4 h-4 mr-2"
                                        />
                                        Test Connection
                                    </Button>

                                    {/* Test Connection Result */}
                                    {testConnectionResult && (
                                        <div>
                                            <Message
                                                success={
                                                    testConnectionResult.success
                                                }
                                                error={
                                                    !testConnectionResult.success
                                                }
                                            >
                                                {testConnectionResult.message}
                                            </Message>
                                        </div>
                                    )}
                                </div>
                            )}

                        {/* Test Connection for Odoo integrations */}
                        {type.id === "odoo" && (
                            <div className="space-y-3">
                                <Button
                                    variant="outline"
                                    onClick={handleTestConnection}
                                    disabled={
                                        testConnectionLoading ||
                                        !areRequiredOdooFieldsFilled()
                                    }
                                    isLoading={testConnectionLoading}
                                    className="w-full"
                                >
                                    <Icon
                                        name="Database"
                                        className="w-4 h-4 mr-2"
                                    />
                                    Test Connection
                                </Button>

                                {/* Test Connection Result */}
                                {testConnectionResult && (
                                    <div>
                                        <Message
                                            success={
                                                testConnectionResult.success
                                            }
                                            error={
                                                !testConnectionResult.success
                                            }
                                        >
                                            <div className="space-y-2">
                                                <p>
                                                    {
                                                        testConnectionResult.message
                                                    }
                                                </p>
                                                {testConnectionResult.success &&
                                                    testConnectionResult.connectionInfo && (
                                                        <div className="text-sm mt-2 space-y-1">
                                                            <p>
                                                                <strong>
                                                                    Version:
                                                                </strong>{" "}
                                                                {
                                                                    testConnectionResult
                                                                        .connectionInfo
                                                                        .version
                                                                }
                                                            </p>
                                                            {testConnectionResult
                                                                .connectionInfo
                                                                .user && (
                                                                <>
                                                                    <p>
                                                                        <strong>
                                                                            User:
                                                                        </strong>{" "}
                                                                        {
                                                                            testConnectionResult
                                                                                .connectionInfo
                                                                                .user
                                                                                .name
                                                                        }{" "}
                                                                        (
                                                                        {
                                                                            testConnectionResult
                                                                                .connectionInfo
                                                                                .user
                                                                                .login
                                                                        }
                                                                        )
                                                                    </p>
                                                                    <p>
                                                                        <strong>
                                                                            Company:
                                                                        </strong>{" "}
                                                                        {
                                                                            testConnectionResult
                                                                                .connectionInfo
                                                                                .user
                                                                                .company
                                                                        }
                                                                    </p>
                                                                </>
                                                            )}
                                                            {testConnectionResult
                                                                .connectionInfo
                                                                .stats && (
                                                                <p>
                                                                    <strong>
                                                                        Records:
                                                                    </strong>{" "}
                                                                    {
                                                                        testConnectionResult
                                                                            .connectionInfo
                                                                            .stats
                                                                            .partners
                                                                    }{" "}
                                                                    partners
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                            </div>
                                        </Message>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end space-x-2 pt-4 flex-shrink-0">
                        <Button onClick={onClose} variant="outline">
                            Cancel
                        </Button>
                        {showOAuthButton ? (
                            <Button
                                onClick={() => handleCreate(type)}
                                disabled={isLoading || isLoadingExisting}
                                isLoading={isLoading || isLoadingExisting}
                            >
                                Connect with {integrationConfig.name}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => handleCreate(type)}
                                disabled={
                                    isLoading ||
                                    isLoadingExisting ||
                                    Object.entries(
                                        integrationConfig.configFields
                                    )
                                        .filter(([key, field]) =>
                                            shouldShowField(field, key)
                                        )
                                        .some(
                                            ([key, field]) =>
                                                field.required &&
                                                !selectedType.config[key]
                                        )
                                }
                                isLoading={isLoading || isLoadingExisting}
                            >
                                {isShopify
                                    ? isEditing
                                        ? "Update"
                                        : "Connect with Shopify"
                                    : isEditing
                                    ? "Update"
                                    : "Create"}
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        );
    };

    if (selectedType) {
        const restriction = RESTRICTED_TYPES[selectedType.id];
        if (restriction && !restriction.hasAccess) {
            return (
                <Dialog open={true} onOpenChange={onClose}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Crown className="w-5 h-5 text-purple-600" />
                                Upgrade Required
                            </DialogTitle>
                            <DialogDescription>
                                {restriction.label} integration is available on Innovator plan ($100/month) and above. Upgrade your plan to use this integration.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex justify-end pt-4">
                            <Button onClick={onClose} variant="outline">
                                Close
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            );
        }
        return renderConfigurationForm(selectedType);
    }

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle>New App</DialogTitle>
                    <DialogDescription>
                        Choose an integration to add to your workspace
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4 px-1">
                    <input
                        type="text"
                        placeholder="Search for a new integration"
                        className="w-full p-2 border rounded-md"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {filteredTypes.map((type) => {
                            const restriction = RESTRICTED_TYPES[type.id];
                            const isRestricted = restriction && !restriction.hasAccess;

                            return (
                                <button
                                    key={type.id}
                                    className={`text-center p-4 border border-gray-100 shadow-md transition-shadow rounded-md aspect-video flex flex-col justify-center items-center space-y-2 relative ${
                                        isRestricted
                                            ? "opacity-60 cursor-not-allowed"
                                            : "hover:shadow-lg"
                                    }`}
                                    onClick={() => handleCreate(type)}
                                    disabled={isLoading || isRestricted}
                                >
                                    {isRestricted && (
                                        <span className="absolute top-1 right-1 flex items-center gap-1 text-[10px] text-purple-600 font-medium bg-purple-50 px-1.5 py-0.5 rounded">
                                            <Crown className="w-3 h-3" />
                                            Innovator+
                                        </span>
                                    )}
                                    {type.imageIcon ? (
                                        <img
                                            src={type.imageIcon}
                                            alt={`${type.name} icon`}
                                            className="w-10 h-10 object-contain"
                                        />
                                    ) : (
                                        <Icon
                                            name={type.icon}
                                            className="w-10 h-10"
                                        />
                                    )}
                                    <span className="font-medium text-sm">
                                        {type.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-end pt-4 flex-shrink-0 border-t border-gray-200 bg-gray-50 -mx-6 px-6 py-4">
                    <Button onClick={onClose} variant="outline">
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
